import * as React from 'react'
import { ReactElement } from 'react'
import { FieldArray, useFormikContext } from 'formik'
import { FileMutationDto } from '../../../../config/fileConfig'
import { Stack } from '@fluentui/react'
import TierList from '../../Tier/TierList'
import { Field } from '@fluentui/react-components'
import MoneyInput from '../../../Input/MoneyInput'
import { InsolvencyModuleDto, useFileModule } from '../../../../hooks/module/useFileModule'
import { ModuleType } from '../../../../context/GlobalContext'
import { RateFieldProps } from './RateFields'
import { UserInsolvencyTierDto } from '../../../../config/finance/financeConfig'
import { UserInsolvencyTierListItem } from '../../Tier/UserInsolvencyTierListItem'
import UserInsolvencyTierFormContainer, {
    userInsolvencyTierValidationSchema,
} from '../../Tier/UserInsolvencyTierFormContainer'
import { getDefaultTierValue } from './RateFieldsContents'
import FormattableNumberInput, { CommitEventType } from '../../../Input/FormattableNumberInput'
import { useTranslation } from 'react-i18next'

function getDefaultInsolvencyTierValue(currency: string, insolvencyModule: InsolvencyModuleDto): UserInsolvencyTierDto {
    return {
        ...getDefaultTierValue(currency),
        type: 'USER_INSOLVENCY_TIER',
        role: undefined,
        estateRate: insolvencyModule?.rateEstate || '100.00',
        factorExperience: '1.0',
        factorEstate: insolvencyModule?.factorEstate || '1.0',
        factorTotal: insolvencyModule?.factorEstate || '1.0',
    }
}

export default function RateFieldsInsolvencyContents({ orientation }: RateFieldProps): ReactElement {
    const { t: translate } = useTranslation()

    const { values, setFieldValue } = useFormikContext<FileMutationDto<UserInsolvencyTierDto>>()
    const { fileModule: insolvencyModule, index: moduleNumber } = useFileModule<InsolvencyModuleDto>(
        ModuleType.Insolvency,
        values,
    )

    return (
        <>
            {insolvencyModule && (
                <>
                    <Field label={translate('file.edit.form.finance.rates.factorestate')} orientation={orientation}>
                        <FormattableNumberInput
                            allowNegative={false}
                            decimals={1}
                            value={insolvencyModule?.factorEstate}
                            onCommit={(eventType: CommitEventType, value: string) => {
                                if (eventType !== CommitEventType.OnLoad) {
                                    setFieldValue(`modules[${moduleNumber}].factorEstate`, value)
                                }
                            }}
                        />
                    </Field>
                    <Field label={translate('file.edit.form.finance.rates.rateestate')} orientation={orientation}>
                        <MoneyInput
                            value={insolvencyModule?.rateEstate}
                            currency={values.currency}
                            onCommit={(eventType: CommitEventType, value: string) => {
                                if (eventType !== CommitEventType.OnLoad) {
                                    setFieldValue(`modules[${moduleNumber}.rateEstate]`, value)
                                }
                            }}
                        />
                    </Field>
                </>
            )}
            <Stack>
                <FieldArray name="tiers">
                    {() => (
                        <TierList<FileMutationDto<UserInsolvencyTierDto>, UserInsolvencyTierDto>
                            labelPrefixKey={'file.edit.form.finance.rates.tier'}
                            defaultTierValue={() => getDefaultInsolvencyTierValue(values.currency, insolvencyModule)}
                            parentValues={values}
                            createListItem={(index: number, item: UserInsolvencyTierDto, editTier) => (
                                <UserInsolvencyTierListItem
                                    index={index}
                                    item={item}
                                    editTier={editTier}
                                    insolvencyModule={insolvencyModule}
                                />
                            )}
                            createTierFormContainer={props => <UserInsolvencyTierFormContainer {...props} />}
                            validationSchema={userInsolvencyTierValidationSchema}
                        />
                    )}
                </FieldArray>
            </Stack>
        </>
    )
}
